import { ChangeEvent, useCallback, useEffect, useMemo } from 'react';
import { styled, TextField } from '@mui/material';
import { Grid } from '@mui/system';
import { isEmpty } from 'validate.js';
import { Allocation } from 'common/types/allocation';
import { FormDialog } from 'components';
import { AllocationVersionDialogProps } from 'pages/ValuationsAllocation/types';
import { useUpdateAllocationInfo } from 'services/hooks/allocation';
import { useFormValidation } from 'services/hooks/useFormValidation';

const PREFIX = 'AllocationDialog';

const classes = {
  margin: `${PREFIX}-margin`,
};

const StyledFormDialog = styled(FormDialog)({
  [`& .${classes.margin}`]: {
    marginBottom: 20,
  },
});

const constraints = {
  name: {
    presence: { allowEmpty: false, message: 'is required' },
    length: { minimum: 3, message: 'must be at least 3 characters' },
  },
};

const AllocationDialog = (props: AllocationVersionDialogProps) => {
  const { open = false, onClose = () => {}, versions, getVersions, allocationInfo, setAllocationInfo } = props;

  const { formValidated, validateForm } = useFormValidation(constraints);
  const { values } = formValidated;

  const [, updateAllocation] = useUpdateAllocationInfo();

  useEffect(() => {
    if (!isEmpty(allocationInfo)) {
      const formValues = {
        name: allocationInfo?.name,
      };

      validateForm(formValues);
    }
  }, [allocationInfo, validateForm]);

  const editMode = useMemo(() => allocationInfo && !!allocationInfo.id, [allocationInfo]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    event.persist();

    validateForm({
      ...values,
      [event.target.name]: event.target.value,
    });
  };

  const handleSave = useCallback(() => {
    updateAllocation(allocationInfo?.id, {
      ...allocationInfo,
      ...values,
    }).then((updatedAllocationInfo: Allocation) => {
      setAllocationInfo(updatedAllocationInfo);
      const currentVersion = versions?.find(version => version.id === updatedAllocationInfo.id);
      getVersions({
        ...currentVersion,
        name: updatedAllocationInfo.name,
        slug: updatedAllocationInfo.slug,
      });
    });
  }, [updateAllocation, allocationInfo, values, setAllocationInfo, versions, getVersions]);

  const onBlur = () => {
    if (typeof values.name === 'string' && !values.name.length) {
      validateForm({
        ...values,
        name: allocationInfo?.name,
      });
    }
  };

  return (
    <StyledFormDialog
      open={open}
      title={`Version ${allocationInfo?.version}`}
      editMode={editMode}
      isValid={formValidated.isValid}
      onSave={handleSave}
      onClose={onClose}>
      <Grid container>
        <TextField
          fullWidth
          id="name"
          name="name"
          type="text"
          label="Name"
          onChange={handleChange}
          className={classes.margin}
          value={values.name || ''}
          onBlur={onBlur}
        />
      </Grid>
    </StyledFormDialog>
  );
};

export default AllocationDialog;
